import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Award, Calendar } from 'lucide-react';
import { getUserTestResults, TestPerformanceData } from '../../services/analyticsService';

interface TestHistoryTimelineProps {
  userId: string;
  limit?: number;
  compact?: boolean;
}

const TestHistoryTimeline: React.FC<TestHistoryTimelineProps> = ({
  userId,
  limit = 10,
  compact = false
}) => {
  const [tests, setTests] = useState<TestPerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchTests = async () => {
      setLoading(true);
      const testData = await getUserTestResults(userId);
      setTests(testData);
      setLoading(false);
    };

    if (userId) {
      fetchTests();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-3 text-gray-600">Loading test history...</span>
        </div>
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No test history yet</p>
          <p className="text-sm text-gray-500 mt-2">Your completed tests will appear here!</p>
        </div>
      </div>
    );
  }

  const displayTests = showAll ? tests : tests.slice(0, limit);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 70) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (score >= 40) return <Award className="w-5 h-5 text-yellow-600" />;
    return <XCircle className="w-5 h-5 text-red-600" />;
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Pass';
    if (score >= 40) return 'Below Average';
    return 'Needs Improvement';
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${compact ? 'p-4' : 'p-6'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Clock className="w-6 h-6 text-red-600" />
          <h2 className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
            Test History
          </h2>
        </div>
        <span className="text-sm text-gray-600">{tests.length} total tests</span>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* Timeline items */}
        <div className="space-y-6">
          {displayTests.map((test, index) => {
            const finalScore = test.totalScore || test.score;
            const hasInterview = test.interviewScore !== undefined;

            return (
              <div key={index} className="relative pl-16">
                {/* Timeline dot */}
                <div className="absolute left-3 top-2 w-6 h-6 rounded-full bg-white border-2 border-red-600 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-red-600"></div>
                </div>

                {/* Content card */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-red-300 transition-all hover:shadow-md">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{test.testTitle}</h3>
                      <div className="flex items-center space-x-2 text-xs text-gray-600">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(test.completedAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>

                    {/* Score badge */}
                    <div className="flex items-center space-x-2">
                      {getScoreIcon(finalScore)}
                      <div className={`px-3 py-1 rounded-full border font-semibold ${getScoreColor(finalScore)}`}>
                        {Math.round(finalScore)}%
                      </div>
                    </div>
                  </div>

                  {/* Score breakdown */}
                  {hasInterview && test.mcqScore !== undefined && test.interviewScore !== undefined ? (
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-600 mb-1">MCQ Score</p>
                        <p className="text-lg font-bold text-gray-900">{Math.round(test.mcqScore)}%</p>
                        <div className="h-1 bg-gray-200 rounded-full mt-2 overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${test.mcqScore}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-600 mb-1">Interview Score</p>
                        <p className="text-lg font-bold text-gray-900">{Math.round(test.interviewScore)}%</p>
                        <div className="h-1 bg-gray-200 rounded-full mt-2 overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full transition-all"
                            style={{ width: `${test.interviewScore}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all"
                          style={{ width: `${finalScore}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getScoreColor(finalScore)}`}>
                      {getScoreLabel(finalScore)}
                    </span>

                    {test.timeTaken && (
                      <div className="flex items-center space-x-1 text-xs text-gray-600">
                        <Clock className="w-3 h-3" />
                        <span>{test.timeTaken} min</span>
                      </div>
                    )}

                    {hasInterview && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                        Full Assessment
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Show More Button */}
      {tests.length > limit && (
        <div className="mt-6 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            {showAll ? 'Show Less' : `Show ${tests.length - limit} More Tests`}
          </button>
        </div>
      )}
    </div>
  );
};

export default TestHistoryTimeline;
